import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles, Leaf, HeartHandshake, CalendarHeart, ArrowUpRight, ArrowRight, CheckCircle2,
  MapPin, Instagram, MessageCircle, Phone, HelpCircle, Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { TREATMENTS, CONCERNS, FAQS } from "@/data/treatments";
import TreatmentModal from "@/components/TreatmentModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_unified-calendar-app-2/artifacts/7wohqirg_Texto%20del%20pa%CC%81rrafo.jpeg";

const BEFORE_AFTER = [
  {
    label: "Glow + textura",
    before: "https://images.unsplash.com/photo-1605497788044-5a32c7078486?auto=format&fit=crop&w=600&q=80",
    after: "https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=600&q=80",
  },
  {
    label: "Manchas atenuadas",
    before: "https://images.unsplash.com/photo-1614108015543-8e818b78f3c1?auto=format&fit=crop&w=600&q=80",
    after: "https://images.unsplash.com/photo-1612817288484-6f916006741a?auto=format&fit=crop&w=600&q=80",
  },
  {
    label: "Piel renovada",
    before: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?auto=format&fit=crop&w=600&q=80",
    after: "https://images.unsplash.com/photo-1571781926291-c477ebfd024b?auto=format&fit=crop&w=600&q=80",
  },
];

export default function Landing() {
  const [selectedConcern, setSelectedConcern] = useState("Glow");
  const [modalTreatment, setModalTreatment] = useState(null);
  const recommendedTreatments = TREATMENTS.filter((t) => t.concerns?.includes(selectedConcern));

  return (
    <div className="min-h-screen bg-[#F4EFE4]">
      {/* Nav */}
      <nav className="sticky top-0 z-50 backdrop-blur-md bg-[#F4EFE4]/80 border-b border-[#E8DFD2]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <img src={LOGO_URL} alt="Amarme Skin" className="h-10 w-10 rounded-full object-cover border border-[#B5704A]/20" />
            <div className="leading-none">
              <div className="brand-wordmark text-base text-[#3A2418]">AMARME</div>
              <div className="font-script text-xs text-[#B5704A] -mt-0.5">Skin</div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#servicios" className="text-sm text-[#3A2418] hover:text-[#B5704A] transition-colors" data-testid="nav-services">Tratamientos</a>
            <a href="#que-trabajamos" className="text-sm text-[#3A2418] hover:text-[#B5704A] transition-colors" data-testid="nav-concerns">¿Qué trabajamos?</a>
            <a href="#faq" className="text-sm text-[#3A2418] hover:text-[#B5704A] transition-colors" data-testid="nav-faq">FAQ</a>
            <a href="#contacto" className="text-sm text-[#3A2418] hover:text-[#B5704A] transition-colors" data-testid="nav-contact">Contacto</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-xs text-[#6F5A4C] hover:text-[#B5704A] hidden sm:inline" data-testid="landing-staff-login">Acceso staff</Link>
            <Link to="/reservar" data-testid="landing-cta-top">
              <Button className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full">
                Agendar cita <ArrowUpRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.span initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="label-eyebrow text-[#B5704A]">
              porque cuidar tu piel también es amarte · Chile
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-[400] text-[#3A2418] mt-4 leading-[1.02]">
              Tu piel <span className="italic font-[500] text-[#B5704A]">merece amor.</span><br />
              Nosotras te lo damos.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
              className="text-base md:text-base lg:text-lg text-[#6F5A4C] mt-6 max-w-xl leading-relaxed">
              En Amarme Skin diseñamos protocolos personalizados de cuidado facial. Tecnología, ingredientes activos y manos expertas en un espacio creado para tu calma.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }} className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/reservar" data-testid="landing-cta-primary">
                <Button size="lg" className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full px-8 h-12">Agendar cita</Button>
              </Link>
              <a href="#servicios" className="text-sm font-medium text-[#3A2418] hover:text-[#B5704A] transition-colors">Ver tratamientos →</a>
            </motion.div>
            <div className="mt-12 flex items-center gap-8 text-sm text-[#6F5A4C]">
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#B5704A]" />Protocolos a medida</span>
              <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#B5704A]" />Tecnología profesional</span>
            </div>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-6 bg-[#E8DFD2]/60 rounded-[3rem] rotate-3"></div>
              <img src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=800&q=80" alt="Tratamiento facial Amarme Skin" className="relative rounded-[2rem] border border-[#E8DFD2] w-full aspect-[4/5] object-cover shadow-lg" />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-4 shadow-xl border border-[#E8DFD2]">
                <img src={LOGO_URL} alt="" className="w-12 h-12 rounded-full object-cover" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brand strip */}
      <section className="py-8 border-y border-[#E8DFD2] bg-[#FAF7F1]">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap justify-center items-center gap-x-12 gap-y-4 text-[#6F5A4C]">
          {["Hydrafacial", "Dermapen", "Radiofrecuencia", "Peelings médicos"].map((b) => (
            <span key={b} className="font-display text-lg italic" data-testid={`brand-${b}`}>{b}</span>
          ))}
        </div>
      </section>

      {/* ¿Qué trabajamos? */}
      <section id="que-trabajamos" className="bg-[#FAF7F1] py-24 border-y border-[#E8DFD2]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-4 mb-10 items-end">
            <div className="md:col-span-2">
              <span className="label-eyebrow text-[#B5704A]">¿Qué trabajamos?</span>
              <h2 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-3">
                Encuentra el tratamiento para <span className="italic font-[500] text-[#B5704A]">tu necesidad</span>
              </h2>
              <p className="text-[#6F5A4C] mt-4 max-w-xl">
                Selecciona tu principal preocupación y te recomendamos los protocolos ideales.
              </p>
            </div>
          </div>

          {/* Concern buttons */}
          <div className="flex flex-wrap gap-2 mb-10">
            {CONCERNS.map((c) => (
              <button
                key={c}
                onClick={() => setSelectedConcern(c)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium border transition-all ${
                  selectedConcern === c
                    ? "bg-[#B5704A] text-white border-[#B5704A]"
                    : "bg-white text-[#3A2418] border-[#E8DFD2] hover:border-[#B5704A] hover:text-[#B5704A]"
                }`}
                data-testid={`concern-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {/* Recommended treatments */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recommendedTreatments.length === 0 ? (
              <div className="md:col-span-3 surface-card p-12 text-center bg-white">
                <p className="text-[#6F5A4C]">Para esta preocupación recomendamos un diagnóstico personalizado.</p>
                <a href="https://wa.me/56973842894?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20mi%20diagn%C3%B3stico%20en%20Amarme%20Skin%20Studio" target="_blank" rel="noopener noreferrer" className="inline-block mt-4">
                  <Button className="bg-[#B5704A] hover:bg-[#9C5E3D] hover:text-white text-white rounded-full">Agendar diagnóstico</Button>
                </a>
              </div>
            ) : (
              recommendedTreatments.map((t) => (
                <button
                  key={t.slug}
                  onClick={() => setModalTreatment(t)}
                  className="surface-card surface-card-hover p-5 bg-white flex items-center gap-4 group text-left w-full"
                  data-testid={`recommended-${t.slug}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#B5704A]/10 flex items-center justify-center group-hover:bg-[#B5704A] transition-colors shrink-0">
                    <Sparkles className="w-5 h-5 text-[#B5704A] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg font-[500] text-[#3A2418] truncate">{t.title}</div>
                    <div className="text-xs text-[#6F5A4C] flex items-center gap-1 mt-0.5"><Clock className="w-3 h-3" />{t.duration}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-[#B5704A] group-hover:translate-x-1 transition-transform" />
                </button>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="servicios" className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid md:grid-cols-3 gap-4 mb-12 items-end">
          <div className="md:col-span-2">
            <span className="label-eyebrow text-[#B5704A]">Tratamientos</span>
            <h2 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-3">
              Cada piel <span className="italic font-[500] text-[#B5704A]">tiene su historia</span>
            </h2>
            <p className="text-[#6F5A4C] mt-4 max-w-xl">
              Por eso ninguna sesión es igual. Diagnosticamos, escuchamos y diseñamos el ritual perfecto para ti.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TREATMENTS.map((t, i) => (
            <motion.div
              key={t.slug}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <button
                type="button"
                onClick={() => setModalTreatment(t)}
                className="surface-card surface-card-hover p-6 group block h-full w-full text-left"
                data-testid={`treatment-${t.slug}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-9 h-9 rounded-full bg-[#B5704A]/10 flex items-center justify-center group-hover:bg-[#B5704A] transition-colors">
                    <Sparkles className="w-4 h-4 text-[#B5704A] group-hover:text-white transition-colors" />
                  </div>
                  <span className="label-eyebrow text-[10px] text-[#B5704A] flex items-center gap-1"><Clock className="w-3 h-3" />{t.duration}</span>
                </div>
                <h3 className="font-display text-2xl font-[500] text-[#3A2418]">{t.title}</h3>
                <p className="text-sm text-[#6F5A4C] mt-2 leading-relaxed line-clamp-3">{t.tagline}</p>
                <div className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-[#B5704A] group-hover:gap-2 transition-all">
                  Ver detalles <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </button>
            </motion.div>
          ))}

          {/* "No sé qué elegir" card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.4 }}
            style={{ backgroundColor: "#3A2418" }}
            className="p-6 rounded-lg border border-[#3A2418] flex flex-col justify-between"
            data-testid="not-sure-cta"
          >
            <div>
              <div className="w-9 h-9 rounded-full bg-[#FAE0CE]/15 flex items-center justify-center mb-4">
                <HelpCircle className="w-4 h-4 text-[#FAE0CE]" />
              </div>
              <h3 className="font-display text-2xl font-[500]" style={{ color: "#FFFFFF" }}>¿No sabes qué elegir?</h3>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: "#F4EFE4" }}>
                Si no sabes qué tratamiento necesita tu piel, agenda tu diagnóstico y te guiaremos paso a paso.
              </p>
            </div>
            <a
              href="https://wa.me/56973842894?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20mi%20diagn%C3%B3stico%20en%20Amarme%20Skin%20Studio"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-[#FAE0CE] text-[#3A2418] px-5 py-3 rounded-full font-medium hover:bg-white transition-colors w-fit"
              data-testid="evaluation-cta"
            >
              Agendar cita <ArrowUpRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* About / Studio */}
      <section id="estudio" className="bg-[#3A2418] text-[#F4EFE4] py-24 grain">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <span className="label-eyebrow text-[#E8B79A]">El estudio</span>
            <h2 className="font-display text-4xl sm:text-5xl font-[300] mt-3 leading-[1.05]">
              Un espacio para <span className="italic font-[500] text-[#E8B79A]">reconectar</span> con tu piel.
            </h2>
            <p className="text-[#E8DFD2]/80 mt-6 leading-relaxed">
              Amarme Skin nace del deseo de ofrecerle a las mujeres un lugar donde el cuidado
              facial sea también un ritual de amor propio. Mezclamos lo último en tecnología
              estética con la calidez de un trato humano, sin prisa ni protocolos genéricos.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-6">
              <div>
                <div className="font-display text-4xl font-[500] text-[#E8B79A]">+500</div>
                <div className="label-eyebrow text-[#E8DFD2]/70 mt-2">Pieles felices</div>
              </div>
              <div>
                <div className="font-display text-4xl font-[500] text-[#E8B79A]">100%</div>
                <div className="label-eyebrow text-[#E8DFD2]/70 mt-2">Personalizado</div>
              </div>
              <div>
                <div className="font-display text-4xl font-[500] text-[#E8B79A]">+4</div>
                <div className="label-eyebrow text-[#E8DFD2]/70 mt-2">Protocolos</div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src="https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?auto=format&fit=crop&w=400&q=80" alt="Mascarilla facial profesional" className="rounded-2xl object-cover aspect-[3/4]" />
            <img src="https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?auto=format&fit=crop&w=400&q=80" alt="Tratamiento facial relajante" className="rounded-2xl object-cover aspect-[3/4] mt-8" />
          </div>
        </div>
      </section>

      {/* Antes y después */}
      <section id="resultados" className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center mb-12">
          <span className="label-eyebrow text-[#B5704A]">Resultados</span>
          <h2 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-3">
            Antes <span className="italic font-[500] text-[#B5704A]">y después</span>
          </h2>
          <p className="text-[#6F5A4C] mt-4 max-w-xl mx-auto">
            Pequeñas transformaciones de nuestras clientas. Cada piel tiene su tiempo y nosotras lo respetamos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BEFORE_AFTER.map((ba, i) => (
            <motion.div
              key={ba.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="surface-card overflow-hidden"
              data-testid={`before-after-${i}`}
            >
              <div className="grid grid-cols-2">
                <div className="relative">
                  <img src={ba.before} alt={`Antes - ${ba.label}`} className="w-full aspect-square object-cover" />
                  <span className="absolute top-2 left-2 bg-[#3A2418]/80 text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">Antes</span>
                </div>
                <div className="relative">
                  <img src={ba.after} alt={`Después - ${ba.label}`} className="w-full aspect-square object-cover" />
                  <span className="absolute top-2 right-2 bg-[#B5704A] text-white text-[10px] uppercase tracking-widest px-2 py-1 rounded-full">Después</span>
                </div>
              </div>
              <div className="p-4 text-center">
                <p className="font-display text-lg italic text-[#3A2418]">{ba.label}</p>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="text-xs text-[#6F5A4C] text-center mt-8 italic">
          * Imágenes referenciales. Resultados varían según cada piel y protocolo.
        </p>
      </section>

      {/* Values strip */}
      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8 border-t border-[#E8DFD2]">
        {[
          {
            emoji: "✨",
            title: "Tecnología & dermocosmética",
            desc: "Protocolos respaldados por ciencia y cuidado consciente de la piel.",
          },
          {
            emoji: "🤍",
            title: "Experiencia personalizada",
            desc: "Tu piel, tus objetivos y un plan diseñado especialmente para ti.",
          },
          {
            emoji: "📅",
            title: "Resultados progresivos",
            desc: "El verdadero cambio se construye con constancia y seguimiento.",
          },
        ].map((v) => (
          <div key={v.title} className="text-center px-4" data-testid={`value-${v.title}`}>
            <div className="text-3xl mb-4">{v.emoji}</div>
            <h3 className="font-display text-2xl font-[500] text-[#3A2418]">{v.title}</h3>
            <p className="text-sm text-[#6F5A4C] mt-2 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-[#FAF7F1] py-24 border-y border-[#E8DFD2]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="label-eyebrow text-[#B5704A]">FAQ</span>
            <h2 className="font-display text-4xl sm:text-5xl font-[300] text-[#3A2418] mt-3">
              Preguntas <span className="italic font-[500] text-[#B5704A]">frecuentes</span>
            </h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`} className="surface-card px-5 border-[#E8DFD2]">
                <AccordionTrigger className="text-left font-display text-lg font-[500] text-[#3A2418] hover:text-[#B5704A] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#6F5A4C] leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact CTA */}
      <section id="contacto" className="max-w-7xl mx-auto px-6 py-24">
        <div
          style={{ backgroundColor: "#3A2418" }}
          className="rounded-[2rem] p-12 lg:p-16 grain relative overflow-hidden"
        >
          <div className="absolute -right-20 -top-20 w-80 h-80 rounded-full bg-[#B5704A]/20"></div>
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="label-eyebrow" style={{ color: "#FAE0CE" }}>Agenda</span>
              <h2 className="font-display text-4xl lg:text-5xl font-[400] mt-3 leading-tight" style={{ color: "#FFFFFF" }}>
                Agendemos tu <span className="italic font-[600]" style={{ color: "#FAE0CE" }}>próximo ritual.</span>
              </h2>
              <p className="mt-4 leading-relaxed" style={{ color: "#F4EFE4" }}>
                Agenda online en segundos o escríbenos por WhatsApp e Instagram. Te ayudamos a elegir el tratamiento ideal.
              </p>
            </div>
            <div className="space-y-3">
              <Link to="/reservar" className="flex items-center justify-between bg-[#FAE0CE] text-[#3A2418] rounded-full px-6 py-4 hover:bg-white transition-colors group" data-testid="cta-book-online">
                <span className="flex items-center gap-3 font-medium"><CalendarHeart className="w-5 h-5" />Agendar online</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a href="https://wa.me/56973842894?text=Hola%2C%20me%20gustar%C3%ADa%20agendar%20una%20cita%20en%20Amarme%20Skin%20Studio" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-[#B5704A] rounded-full px-6 py-4 hover:bg-[#9C5E3D] transition-colors group" style={{ color: "#FFFFFF" }} data-testid="cta-whatsapp">
                <span className="flex items-center gap-3 font-medium"><MessageCircle className="w-5 h-5" />WhatsApp</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a href="https://www.instagram.com/amarmeskin" target="_blank" rel="noopener noreferrer" className="flex items-center justify-between bg-[#B5704A] rounded-full px-6 py-4 hover:bg-[#9C5E3D] transition-colors group" style={{ color: "#FFFFFF" }} data-testid="cta-instagram">
                <span className="flex items-center gap-3 font-medium"><Instagram className="w-5 h-5" />@amarmeskin</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a href="tel:+56973842894" className="flex items-center justify-between bg-[#B5704A] rounded-full px-6 py-4 hover:bg-[#9C5E3D] transition-colors group" style={{ color: "#FFFFFF" }} data-testid="cta-phone">
                <span className="flex items-center gap-3 font-medium"><Phone className="w-5 h-5" />+56 9 3093 1044</span>
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E8DFD2] py-10 bg-[#FAF7F1]">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-8 text-sm text-[#6F5A4C]">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <img src={LOGO_URL} alt="" className="w-10 h-10 rounded-full object-cover" />
              <div>
                <div className="brand-wordmark text-[#3A2418]">AMARME</div>
                <div className="font-script text-[#B5704A] text-xs -mt-0.5">Skin</div>
              </div>
            </div>
            <p>Recuperación cutánea basada en ciencia.</p>
          </div>
          <div>
            <div className="label-eyebrow text-[#3A2418] mb-3">Visítanos</div>
            <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Chile · Lun a Vie, 9:00 – 19:00 · Sáb, 9:00 – 15:00</p>
          </div>
          <div>
            <div className="label-eyebrow text-[#3A2418] mb-3">Acceso</div>
            <Link to="/login" className="hover:text-[#B5704A] transition-colors" data-testid="footer-staff-login">
              Iniciar sesión (staff) →
            </Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 mt-8 pt-6 border-t border-[#E8DFD2] text-xs text-[#6F5A4C] flex flex-col sm:flex-row justify-between gap-3">
          <span>© 2026 Amarme Skin</span>
          <div className="flex items-center gap-4">
            <Link to="/privacidad" className="hover:text-[#B5704A] transition-colors" data-testid="footer-privacy">
              Política de privacidad
            </Link>
            <span className="text-[#E8DFD2]">·</span>
            <Link to="/terminos" className="hover:text-[#B5704A] transition-colors" data-testid="footer-terms">
              Términos de servicio
            </Link>
          </div>
          <span className="font-script text-base text-[#B5704A]">Hecho con amor</span>
        </div>
      </footer>

      <TreatmentModal
        treatment={modalTreatment}
        open={!!modalTreatment}
        onClose={() => setModalTreatment(null)}
      />
    </div>
  );
}
